import { useEffect } from 'react';

/**
 * Scroll synchronisation for live sessions.
 *
 * The teacher is the driver: as they scroll, `useScrollBroadcast` emits the
 * scroll position as a percentage (0–100) of the scrollable range. The
 * student follows via `useScrollFollow`, which scrolls its own container to
 * the same percentage. A ratio (rather than pixel offset) is used so the two
 * sides stay in sync despite different viewport sizes and scroll containers.
 *
 * Only the non-driver follows incoming events. The channel broadcasts back to
 * the sender too, so if the driver also followed, applying its own echoed
 * position would re-fire the scroll listener and create a feedback loop. The
 * channel additionally guards `scroll_sync` to teachers only, so a student
 * never broadcasts even if `useScrollBroadcast` were enabled on their side.
 *
 * The context-questions passage (`ChapterContextReader`, tagged
 * `data-live-scroll`) scrolls inside its own nested overflow container
 * whose scroll events don't bubble. `resolveScrollEl` prefers that tagged
 * element whenever it's present and actually scrollable, so the passage —
 * not the page behind it — is what gets synced while it's on screen.
 * Listening is done in the capture phase (see below), which still sees
 * non-bubbling scroll events fired on that nested element.
 */

const clamp = (n, min, max) => Math.min(max, Math.max(min, n));

// The two live-session pages scroll differently: the teacher's view scrolls
// TeacherShell's overflow `<main>`, while the student lobby scrolls the
// document/window. Resolve whichever element is actually the active scroller
// rather than hard-coding one per page. A tagged `[data-live-scroll]` region
// (e.g. the chapter-context passage) takes priority over both — it's the
// content actually being read together, and its own scroll events don't
// bubble to `<main>` or the window.
function resolveScrollEl() {
  const tagged = document.querySelector('[data-live-scroll]');
  if (tagged && tagged.scrollHeight > tagged.clientHeight + 1) {
    const taggedOverflowY = getComputedStyle(tagged).overflowY;
    if (taggedOverflowY === 'auto' || taggedOverflowY === 'scroll') return tagged;
  }
  const main = document.querySelector('main');
  if (main && main.scrollHeight > main.clientHeight + 1) {
    const overflowY = getComputedStyle(main).overflowY;
    if (overflowY === 'auto' || overflowY === 'scroll') return main;
  }
  return document.scrollingElement || document.documentElement;
}

/**
 * Teacher-side: broadcast the local scroll position (throttled to one emit
 * per animation frame) as `scroll_sync { top_pct }`.
 */
export function useScrollBroadcast(perform, enabled) {
  useEffect(() => {
    if (!enabled || !perform) return;

    let raf = 0;
    const emit = () => {
      raf = 0;
      const el = resolveScrollEl();
      const max = el.scrollHeight - el.clientHeight;
      const topPct = max > 0 ? clamp((el.scrollTop / max) * 100, 0, 100) : 0;
      perform('scroll_sync', { top_pct: topPct });
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(emit);
    };

    // Scroll events don't bubble, so a bubble-phase listener on window would
    // only ever see the window's own scroll. The capture phase still
    // traverses every ancestor down to the actual target first, so a single
    // capturing listener on window sees scrolls fired anywhere in the page —
    // `<main>`, the tagged passage, or the window itself — without needing
    // a listener per candidate element.
    window.addEventListener('scroll', onScroll, { passive: true, capture: true });

    return () => {
      window.removeEventListener('scroll', onScroll, { capture: true });
      if (raf) cancelAnimationFrame(raf);
    };
  }, [enabled, perform]);
}

/**
 * Student-side: apply an incoming scroll position. `incoming` is the latest
 * `{ top_pct, ts }` from the `onScrollSync` handler — the `ts` makes each
 * receive a fresh object so this effect re-runs even when the teacher lands
 * on the same percentage twice.
 */
export function useScrollFollow(incoming, enabled) {
  useEffect(() => {
    if (!enabled || !incoming || typeof incoming.top_pct !== 'number') return;
    const el = resolveScrollEl();
    const max = el.scrollHeight - el.clientHeight;
    if (max <= 0) return;
    el.scrollTop = clamp((incoming.top_pct / 100) * max, 0, max);
  }, [incoming, enabled]);
}
